import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NonPartnerPlayerData, PlayerData } from "../../types";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { type VFC, APIResponse, DomExceptionName } from "types";
import Loader from "components/Loader";
import * as R from "ramda";
import { Form, Field } from "react-final-form";
import Select, { type SingleValue } from "react-select";
import NonPartnerPlayerCard from "./nonPartnerPlayerCard";
import PlayerModal from "./PlayerModal";
import Migrate from "../migrate/Migrate";
import { _ } from "i18n";
import client from "libs/client";
import { useToast } from "ui-kit";
import {GenderOptions} from "features/discover/types";

interface nonPartnerPlayerManagementProps {
	activeMenu: string;
	activeClub: any;
	countries: { [key: string]: string };
}

const nonPartnerPlayerManagement: VFC<nonPartnerPlayerManagementProps> = ({ activeMenu, activeClub, countries }) => {
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showPlayerModal, setShowPlayerModal] = useState(false);
	const [hasMoreData, setHasMoreData] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const showLoaderRef = useRef(showLoader);
	const requestFetchWithLoaderGateRef = useRef<() => void>(() => {});
	const isSubmitted = useRef(false);

	useEffect(() => {
		showLoaderRef.current = showLoader;
	}, [showLoader]);

	const [countrieslist, setCountriesList] = useState<{ value: string; label: string }[]>([]);
	const [clubNonpartnerList, setClubNonpartnerList] = useState<{ value: string; label: string; logo: string | null; country: string | null }[]>([]);
	const [nationalityList, setNationalityList] = useState<{ value: string; label: string }[]>([]);
	const [positionList, setPositionList] = useState<{ value: string; label: string }[]>([]);
	const [selectedPlayer, setSelectedPlayer] = useState<NonPartnerPlayerData | null>(null);
	const [selectedMigrationType, setSelectedMigrationType] = useState<string>("");
	const genders = GenderOptions;

	const [clubData, setClubData] = useState<NonPartnerPlayerData[]>([]);

	const [showMigrate, setShowMigrate] = useState(false);

	const [clubValue, setClubValue] = useState<{ value: any; label: any } | null>(null);
	const [genderValue, setGenderValue] = useState<{ value: string; label: string } | null>(null);
	const start = useRef(0);
	const totalRecords = useRef(0);
	const search = useRef("");
	const [defaultCountry, setDefaultCountry] = useState<{ value: string; label: string } | null>(null);
	const isDefaultCountryEmpty = useRef(true);

	const { addSuccessToast, addErrorToast } = useToast();

	const filters = useRef<any>({
		country: null,
	});

	const playerListFetchEnabledRef = useRef(false);
	const playerModalDropdownsLoadedRef = useRef(false);

	const onDeleteActionClick = (player: any) => {
		setSelectedPlayer(player);
		setShowDeleteModal(true);
	};

	const onEditActionClick = (player: any) => {
		loadPlayerModalDropdowns();
		setSelectedPlayer(player);
		setShowPlayerModal(true);
	};

	const onMigrateActionClick = (player: any, type: string) => {
		loadPlayerModalDropdowns();
		setSelectedPlayer(player);
		setSelectedMigrationType(type);
		setShowMigrate(true);
	};

	const onDelete = async () => {
		if (!selectedPlayer) {
			return;
		}

		const response = await client.deleteNPPlayer.mutation({ playerId: selectedPlayer?.id });
		if (response?.data.status === APIResponse.SUCCESS) {
			if (response?.data.message) {
				addSuccessToast(response?.data.message);
			}
			start.current = 0;
			fetchData();
		} else {
			if (response?.data.message) {
				addErrorToast(response?.data.message);
			}
		}
	};

	const onUpdatePlayer = async (values: NonPartnerPlayerData) => {
		isSubmitted.current = true;
		const response = await client.addUpdateNPPlayer.mutation({ body: { nonPartnerPlayer: values } });
		if (response?.data.status === APIResponse.SUCCESS) {
			if (response?.data.message) {
				addSuccessToast(response?.data.message);
			}
			if (values.id) {
				setClubData((prevData: NonPartnerPlayerData[]) => {
					return prevData.map((data) => {
						if (data.id === values.id) {
							const club = clubNonpartnerList.find((club) => club.value == values.clubNonpartnerId);
							return {
								...values,
								npClubName: club.label,
								npClubLogo: club.logo,
								npClubCountry: club.country,
							};
						}
						return data;
					});
				});
			} else {
				playerListFetchEnabledRef.current = true;
				start.current = 0;
				fetchData();
			}
			setShowPlayerModal(false);
		} else {
			if (response?.data.message) {
				addErrorToast(response?.data.message);
			}
		}
		isSubmitted.current = false;
	};

	const onChangeCountry = (e: SingleValue<{ value: string; label: string }>) => {
		if (e?.value) {
			updateFilter(e.value, "country");
			setDefaultCountry(e);
		} else {
			updateFilter("", "country");
			setDefaultCountry(null);
		}
	};

	const getNonPartnerCountriesData = useCallback(async (signal?: AbortSignal) => {
		try {
			const res = await client.getCountries.query(signal);
			if (signal?.aborted) {
				return;
			}
			if (res) {
				const list = res.data as { value: string; label: string }[];
				if (list.length > 0) {
					setCountriesList(list);
					return list;
				}
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
				return;
			}
			throw e;
		}
	}, []);

	const fetchData = async (loadMore: boolean = false, signal?: AbortSignal) => {
		if (!playerListFetchEnabledRef.current) {
			return;
		}

		if (!hasPlayerListQueryCriteria()) {
			playerListFetchEnabledRef.current = false;
			setShowLoader(false);
			setHasMoreData(false);
			if (!loadMore) {
				setClubData([]);
			}
			return;
		}

		if (isDefaultCountryEmpty.current) {
			await getNonPartnerCountriesData(signal);
			if (signal?.aborted) {
				return;
			}
			start.current = 0;
			isDefaultCountryEmpty.current = false;
		}

		const pageStart = start.current;
		const param = {
			start: start.current,
			length: 10,
			search: search.current,
			...(filters.current.country ? { country: filters.current.country } : {}),
			clubNonpartnerId: filters.current.clubNonpartnerId ? `${filters.current.clubNonpartnerId}` : "",
			gender: filters.current.gender ? filters.current.gender : "",
		};

		try {
			const res = await client.getNPPlayers.query(param, signal);
			if (signal?.aborted) {
				return;
			}
			if (res) {
				const response = res.data;
				if (response.status === APIResponse.SUCCESS) {
					totalRecords.current = response.recordsFiltered;
					setShowLoader(false);
					const batch = response.data ?? [];
					setClubData((prevData) => {
						if (!loadMore) {
							return batch;
						}
						if (R.equals(prevData, batch) === false) {
							return [...new Set([...prevData, ...batch])];
						}
						return prevData;
					});
					start.current = pageStart + batch.length;
					const recordsFiltered = response.recordsFiltered ?? 0;
					setHasMoreData(pageStart + batch.length < recordsFiltered);
				} else {
					setShowLoader(false);
					setHasMoreData(false);
				}
			} else {
				setShowLoader(false);
				setHasMoreData(false);
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
				return;
			}
			setShowLoader(false);
			setHasMoreData(false);
		}
	};

	const fetchDataRef = useRef(fetchData);
	fetchDataRef.current = fetchData;

	const requestFetchWithLoaderGate = () => {
		start.current = 0;
		setHasMoreData(false);
		setClubData([]);
		if (!hasPlayerListQueryCriteria()) {
			playerListFetchEnabledRef.current = false;
			setShowLoader(false);
			return;
		}
		playerListFetchEnabledRef.current = true;
		if (showLoaderRef.current) {
			fetchData();
		} else {
			setShowLoader(true);
		}
	};

	requestFetchWithLoaderGateRef.current = requestFetchWithLoaderGate;

	const isFilterActive = (v: unknown) => v !== null && v !== undefined && v !== "";

	const hasPlayerListQueryCriteria = () => {
		const searchTrimmed = (search.current ?? "").trim();
		return (
			isFilterActive(filters.current.country) ||
			isFilterActive(filters.current.clubNonpartnerId) ||
			searchTrimmed.length > 0
		);
	};

	const updateFilter = (value: string | number | undefined, id: string) => {
		const hadActiveFilter = isFilterActive(filters.current[id]);
		const next =
			value !== undefined && value !== null && value !== "" ? value : null;
		if (next !== null) {
			filters.current[id] = next;
		} else {
			filters.current[id] = null;
		}
		const hasActiveFilter = isFilterActive(filters.current[id]);

		// react-select often fires onChange on mount or when options load; ignore no-op changes.
		const shouldFetchPlayers =
			hasActiveFilter || (hadActiveFilter && !hasActiveFilter);
		if (!shouldFetchPlayers) {
			return;
		}

		requestFetchWithLoaderGate();
	};

	const getClubNonpartnerList = useCallback(async (signal?: AbortSignal) => {
		try {
			const res = await client.getAllNPClubs.query(signal);
			if (signal?.aborted) {
				return;
			}
			const response = res?.data;
			if (response) {
				setClubNonpartnerList(response);
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
				return;
			}
			throw e;
		}
	}, []);

	const getNationalityList = async () => {
		const response = await client.getNationalityList.query().then((res) => {
			return res?.data;
		});
		if (response) {
			setNationalityList(response);
		}
	};

	const getPositionList = async () => {
		const response = await client.getPositionList.query().then((res) => {
			return res?.data;
		});
		if (response) {
			setPositionList(response);
		}
	};

	const loadPlayerModalDropdowns = () => {
		if (playerModalDropdownsLoadedRef.current) {
			return;
		}
		playerModalDropdownsLoadedRef.current = true;
		void getNationalityList();
		void getPositionList();
	};

	const onSearchSubmit = (values: { search?: string }) => {
		search.current = (values.search ?? "").trim();
		if (!hasPlayerListQueryCriteria()) {
			playerListFetchEnabledRef.current = false;
			setHasMoreData(false);
			setClubData([]);
			setShowLoader(false);
			return;
		}
		requestFetchWithLoaderGate();
	};

	useEffect(() => {
		if (!showLoader) {
			return;
		}
		const controller = new AbortController();
		void fetchDataRef.current(false, controller.signal);
		return () => {
			controller.abort();
		};
	}, [showLoader]);

	useEffect(() => {
		if (activeMenu !== "non_partner_player") {
			return;
		}
		window.scrollTo({ top: 0, left: 0 });
		const controller = new AbortController();
		const { signal } = controller;
		void (async () => {
			try {
				await Promise.all([
					getNonPartnerCountriesData(signal),
					getClubNonpartnerList(signal),
				]);
			} catch (e: unknown) {
				if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
					return;
				}
				const message = e instanceof Error ? e.message : undefined;
				if (message) {
					addErrorToast(message);
				}
			}
		})();
		return () => {
			controller.abort();
		};
	}, [activeMenu, addErrorToast, getNonPartnerCountriesData, getClubNonpartnerList]);

	useEffect(() => {
		if (activeClub) {
			filters.current.clubNonpartnerId = activeClub?.id;
			setClubValue({ value: activeClub?.id, label: activeClub?.name });
			requestFetchWithLoaderGateRef.current();
		} else {
			filters.current.clubNonpartnerId = null;
			setClubValue(null);
			requestFetchWithLoaderGateRef.current();
		}
	}, [activeClub]);

	const containerRef = useRef(null);
	const intersectionObserverOptions = useMemo(
		() => ({ root: null, rootMargin: "0px", threshold: 1.0 }),
		[],
	);

	useEffect(() => {
		if (showMigrate) {
			return;
		}

		const element = containerRef.current;
		const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
			const [entry] = entries;
			if (!entry.isIntersecting || !playerListFetchEnabledRef.current) {
				return;
			}
			if (start.current < totalRecords.current) {
				void fetchDataRef.current(true);
			} else {
				setHasMoreData(false);
			}
		}, intersectionObserverOptions);

		if (element) {
			observer.observe(element);
		}

		return () => {
			if (element) {
				observer.unobserve(element);
			}
		};
	}, [intersectionObserverOptions, showMigrate]);

	return (
		<>
			{!showMigrate && (
				<div className="nonpartner-players-container">
					<div className="product-management-heading-container">
						<div className="product-management-heading"> {_("product.management.nonpartner.players")}</div>
						<div className="top-form-container">
							<button
								className="btn btn-light-blue"
								onClick={() => {
									loadPlayerModalDropdowns();
									setShowPlayerModal(true);
									setSelectedPlayer(null);
								}}
							>
								{_("filter.add_player")}
							</button>
						</div>
					</div>
					<div className="form-filter-container">
						<div className="filter-field">
							<label>{_("videos.table.th.country")}</label>
							<Select
								className="react-select"
								isClearable
								options={countrieslist}
								onChange={onChangeCountry}
								value={defaultCountry}
							/>
						</div>
						<div className="filter-field">
							<label>{_("videos.table.th.club")}</label>
							<Select
								className="react-select"
								isClearable
								options={clubNonpartnerList}
								value={clubValue}
								onChange={(e) => {
									setClubValue(e);
									updateFilter(e?.value, "clubNonpartnerId");
								}}
							/>
						</div>
						<div className="filter-field">
							<label>{_("videos.table.th.gender")}</label>
							<Select
								className="react-select"
								isClearable
								options={genders}
								value={genderValue}
								onChange={(e) => {
									setGenderValue(e);
									updateFilter(e?.value, "gender");
								}}
							/>
						</div>
						<div className="filter-field">
							<label> {_("label.search")}</label>
							<Form
								onSubmit={onSearchSubmit}
								render={({ handleSubmit, submitting }) => (
									<>
										<form onSubmit={handleSubmit}>
											<Field name="search" type="text">
												{({ input, meta }) => (
													<>
														<input {...input} placeholder={_("search.name")} />
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</>
												)}
											</Field>
										</form>
									</>
								)}
							/>
						</div>
					</div>
					<div className="club-grid-container">
						{clubData ? (
							clubData.map((player) => {
								return (
									<NonPartnerPlayerCard
										key={player.id}
										player={player}
										countriesList={countries}
										onDeleteActionClick={onDeleteActionClick}
										onEditActionClick={onEditActionClick}
										onMigrateActionClick={onMigrateActionClick}
									/>
								);
							})
						) : (
							<h6 className="modal-msg">{_("info.nodata_available")}</h6>
						)}
					</div>
					<div ref={containerRef}>{hasMoreData && <Loader />}</div>
				</div>
			)}
			{showMigrate && (
				<Migrate
					club={null}
					player={selectedPlayer as unknown as PlayerData}
					countries={countries}
					type={selectedMigrationType}
					onClose={() => {
						setShowMigrate(false);
						playerListFetchEnabledRef.current = true;
						start.current = 0;
						fetchData();
					}}
				/>
			)}
			{showDeleteModal && (
				<DeleteConfirmationModal
					show={showDeleteModal}
					title={`${selectedPlayer?.firstName} ${selectedPlayer?.lastName}`}
					onClose={() => {
						setShowDeleteModal(false);
						setSelectedPlayer(null);
					}}
					onDelete={() => {
						onDelete();
						setShowDeleteModal(false);
					}}
				/>
			)}
			{showPlayerModal && (
				<PlayerModal
					onClose={() => {
						setShowPlayerModal(false);
						setSelectedPlayer(null);
					}}
					onUpdatePlayer={onUpdatePlayer}
					player={selectedPlayer}
					clubs={clubNonpartnerList}
					countriesList={countries}
					nationality={nationalityList}
					position={positionList}
					isSubmitted={isSubmitted}
				/>
			)}
		</>
	);
};

export default nonPartnerPlayerManagement;
